import { useForm } from "react-hook-form";
import { useState, useContext } from "react";
import { useTranslations, LocalizationContext } from "contexts/LocalizationContext";
import { registerLegalPartner } from "services/api";
import getPageUrl from "utils/getPageUrl";
import { trackMixpanel } from "utils/trackingUtils";
import { REGISTRATION_FORM_SUBMITTED, REGISTRATION_FORM_ERROR } from "modules/legal-partners/trackingConsts";
import { Chakra } from "@airhelp/react-ui-components";
import {
  Input,
  Textarea,
  SelectCountries,
  Terms,
  FormSubmitButton,
  FormSuccessMessage,
  FormError,
} from "components/FormControl";

const { Box, VStack, HStack } = Chakra;

const Form = () => {
  const translations = useTranslations();
  const { language } = useContext(LocalizationContext);
  const { handleSubmit, errors, register, formState, watch } = useForm();
  const [formError, setFormError] = useState(false);
  const wasFormSubmittedSucessfully = formState.isSubmitSuccessful && !formError;
  const watchTermsAccepted = watch("agreeWithTerms", false);

  const onSubmit = async (values) => {
    try {
      await registerLegalPartner(values);
      setFormError(false);
      trackMixpanel(REGISTRATION_FORM_SUBMITTED);
    } catch (error) {
      setFormError(true);
      trackMixpanel(REGISTRATION_FORM_ERROR);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack align="left" spacing={6}>
        <Input
          errors={errors}
          name="name"
          label={translations("form_name")}
          placeholder={translations("form_name")}
          register={register({ required: true, maxLength: 50 })}
          required
          disabled={wasFormSubmittedSucessfully}
        />
        <Input
          errors={errors}
          name="lawFirmName"
          label={translations("form_firm_name")}
          placeholder={translations("form_firm_name")}
          register={register({ required: true, maxLength: 50 })}
          required
          disabled={wasFormSubmittedSucessfully}
        />
        <Input
          errors={errors}
          name="email"
          type="email"
          label={translations("form_email")}
          placeholder="email@example.com"
          register={register({ required: true, maxLength: 50 })}
          required
          disabled={wasFormSubmittedSucessfully}
        />
        <Input
          errors={errors}
          name="phone"
          label={translations("form_phone_number")}
          placeholder="+00 123-456-789"
          register={register({ required: true, maxLength: 20 })}
          required
          disabled={wasFormSubmittedSucessfully}
        />
        <Input
          errors={errors}
          name="address"
          label={translations("form_address")}
          placeholder={translations("form_address")}
          register={register({ required: true, maxLength: 100 })}
          required
          disabled={wasFormSubmittedSucessfully}
        />

        <HStack
          spacing={4}
          sx={{
            "div:nth-child(1)": { width: "60%" },
            "div:nth-child(2)": { width: "40%" },
          }}
        >
          <Input
            errors={errors}
            name="city"
            label={translations("form_city")}
            placeholder={translations("form_city")}
            register={register({ required: true, maxLength: 50 })}
            required
            disabled={wasFormSubmittedSucessfully}
          />
          <Input
            errors={errors}
            name="postalCode"
            label={translations("form_postal_code")}
            placeholder={translations("form_postal_code")}
            register={register({ required: true, maxLength: 10 })}
            required
            disabled={wasFormSubmittedSucessfully}
          />
        </HStack>
        <Input
          errors={errors}
          name="state"
          label={`${translations("form_state")} (${translations("form_optional")})`}
          placeholder={translations("form_state")}
          register={register({ maxLength: 50 })}
          disabled={wasFormSubmittedSucessfully}
        />
        <SelectCountries
          errors={errors}
          name="country"
          label={translations("form_country")}
          placeholder={translations("form_country")}
          register={register({ required: true })}
          required
          disabled={wasFormSubmittedSucessfully}
        />
      </VStack>
      <Box mt={16}>
        <Textarea
          errors={errors}
          name="expertise"
          label={translations("form_expertise")}
          placeholder={translations("form_expertise")}
          register={register({ maxLength: 1000 })}
          required
          disabled={wasFormSubmittedSucessfully}
        />
      </Box>
      <Terms
        register={register({ required: true })}
        isDisabled={wasFormSubmittedSucessfully}
        htmlContent={translations("form_agree_to_privacy", {
          "%privacy-link-attrs%": `href="${getPageUrl(language, "privacy")}"`,
        })}
      />
      {formError ? (
        <FormError formError={formError} formErrorText={translations("subscribe_form_submit_error")} id="form-error" />
      ) : null}
      {wasFormSubmittedSucessfully ? (
        <FormSuccessMessage text={translations("form_submit_success")} />
      ) : (
        <FormSubmitButton
          text={translations("form_become_a_partner")}
          isDisabled={!watchTermsAccepted}
          isLoading={formState.isSubmitting}
          mt={4}
          width="full"
          data-testid="form-submit-button"
        />
      )}
    </form>
  );
};

export default Form;
